import { useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { KeyRound, Mail } from 'lucide-react'

import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import logoUrl from '/legato-logo.jpg'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Komunikat przekazany przez inny ekran, np. po udanej zmianie hasła.
  const notice = (location.state as { notice?: string } | null)?.notice ?? null

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      const user = await login(email.trim(), password)
      // Konto po resecie hasła musi je najpierw zmienić.
      if (user.must_change_password) {
        navigate('/zmiana-hasla', { replace: true })
        return
      }
      const from = (location.state as { from?: string } | null)?.from
      navigate(from && from !== '/logowanie' ? from : '/', { replace: true })
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Nie udało się zalogować. Spróbuj ponownie.',
      )
      setPassword('')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Panel marki — burgundowa „kurtyna” z motywem szkoły. */}
      <section className="relative flex flex-col justify-between overflow-hidden bg-primary px-8 py-10 text-on-primary lg:w-[45%] lg:px-14 lg:py-16">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/5"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-black/10"
          aria-hidden="true"
        />

        <div className="relative flex items-center gap-3">
          <img
            src={logoUrl}
            alt="Logo Szkoły Muzyki Legato"
            className="h-12 w-12 rounded-lg object-cover"
            width={48}
            height={48}
          />
          <div>
            <p className="font-headline text-2xl font-bold leading-tight">Legato</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">
              Szkoła Muzyki
            </p>
          </div>
        </div>

        <div className="relative my-12 max-w-md lg:my-0">
          <h1 className="font-headline text-3xl font-bold leading-tight lg:text-5xl">
            System zarządzania szkołą
          </h1>
          <p className="mt-4 text-base leading-relaxed text-white/80">
            Grafiki zajęć, rezerwacja sal, obecności i rozliczenia — wszystkie oddziały w jednym
            miejscu.
          </p>
        </div>

        <p className="relative text-xs text-white/60">
          © {new Date().getFullYear()} Szkoła Muzyki Legato
        </p>
      </section>

      {/* Formularz logowania. */}
      <section className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-md">
          <header className="mb-8">
            <h2 className="font-headline text-3xl font-bold text-secondary">Zaloguj się</h2>
            <p className="mt-2 text-on-surface-variant">
              Wprowadź dane otrzymane od administratora szkoły.
            </p>
          </header>

          {notice && !error && (
            <Alert tone="success" className="mb-6">
              {notice}
            </Alert>
          )}

          {error && (
            <Alert tone="error" className="mb-6">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <TextField
              label="Adres e-mail"
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="imie.nazwisko@legato.edu.pl"
              autoComplete="username"
              leadingIcon={<Mail size={18} />}
              required
              autoFocus
            />

            <TextField
              label="Hasło"
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Twoje hasło"
              autoComplete="current-password"
              leadingIcon={<KeyRound size={18} />}
              required
            />

            <Button type="submit" size="lg" fullWidth isLoading={isSubmitting}>
              {isSubmitting ? 'Logowanie…' : 'Zaloguj się'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-on-surface-variant">
            Nie pamiętasz hasła? Skontaktuj się z administratorem swojego oddziału.
          </p>
        </div>
      </section>
    </div>
  )
}
